package com.arena_web.ui.components.forms

import androidx.compose.runtime.*
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.AppColors
import com.arena_web.ui.theme.toRgbString
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import kotlinx.browser.document
import org.jetbrains.compose.web.css.*

data class DropdownOption(
    val value: String,
    val label: String
)

@Composable
fun Dropdown(
    options: List<DropdownOption>,
    selectedValue: String?,
    onSelect: (DropdownOption) -> Unit,
    modifier: Modifier = Modifier,
    label: String? = null,
    placeholder: String = LocalAppStrings.current.formSelectPlaceholder,
    error: String? = null,
    enabled: Boolean = true
) {
    var isOpen by remember { mutableStateOf(false) }
    val colorMode = ColorMode.current
    val colors = AppColors.getColors(colorMode)
    val selectedOption = options.find { it.value == selectedValue }
    val triggerId = remember { "dropdown-trigger-${label.orEmpty().replace(" ", "-").lowercase()}" }
    var dropdownTop by remember { mutableStateOf(0.0) }
    var dropdownLeft by remember { mutableStateOf(0.0) }
    var dropdownWidth by remember { mutableStateOf(0.0) }

    LaunchedEffect(isOpen) {
        if (isOpen) {
            val el = document.getElementById(triggerId) as? org.w3c.dom.HTMLElement
            el?.getBoundingClientRect()?.let { rect ->
                dropdownTop = rect.bottom + 4.0
                dropdownLeft = rect.left
                dropdownWidth = rect.width
            }
        }
    }

    Column(modifier = modifier.fillMaxWidth().sGap(4)) {
        label?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(14)
                    .color(colors.textSecondary)
                    .sMarginBottom(4)
            )
        }

        // Trigger
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .backgroundColor(colors.surface)
                .sBorderRadius(8)
                .sPadding(12)
                .cursor(if (enabled) Cursor.Pointer else Cursor.NotAllowed)
                .opacity(if (enabled) 1.0 else 0.6)
                .id(triggerId)
                .onClick { if (enabled) isOpen = !isOpen }
                .styleModifier {
                    val borderColor = when {
                        error != null -> AppColors.Error.toRgbString()
                        isOpen -> AppColors.Primary.toRgbString()
                        else -> colors.border.toRgbString()
                    }
                    property("border", "1px solid $borderColor")
                    property("transition", "border-color 200ms ease")
                    property("box-sizing", "border-box")
                    property("user-select", "none")
                }
        ) {
            SpanText(
                text = selectedOption?.label ?: placeholder,
                modifier = Modifier
                    .sFontSize(16)
                    .color(
                        if (selectedOption != null) colors.textPrimary
                        else colors.textTertiary
                    )
            )

            // Arrow
            SpanText(
                text = if (isOpen) "\u25B2" else "\u25BC",
                modifier = Modifier
                    .position(Position.Absolute)
                    .sRight(12)
                    .top(50.percent)
                    .sFontSize(10)
                    .color(colors.textSecondary)
                    .styleModifier {
                        property("transform", "translateY(-50%)")
                    }
            )
        }

        // Dropdown list (fixed position, breaks out of overflow)
        if (isOpen) {
            Box(
                modifier = Modifier
                    .position(Position.Fixed)
                    .top(0.px).left(0.px).right(0.px).bottom(0.px)
                    .zIndex(1001)
                    .onClick { isOpen = false }
            )

            Column(
                modifier = Modifier
                    .position(Position.Fixed)
                    .zIndex(1002)
                    .backgroundColor(colors.surface)
                    .sBorderRadius(8)
                    .overflow(Overflow.Auto)
                    .styleModifier {
                        property("top", "${dropdownTop}px")
                        property("left", "${dropdownLeft}px")
                        property("width", "${dropdownWidth}px")
                        property("max-height", s(200))
                        property("scrollbar-width", "none")
                        property("border", "1px solid ${colors.border.toRgbString()}")
                        property("box-shadow", "0 8px 24px rgba(0, 0, 0, 0.2)")
                    }
            ) {
                options.forEach { option ->
                    val isSelected = option.value == selectedValue

                    Box(
                        modifier = Modifier
                            .fillMaxWidth()
                            .sPadding(10, 12)
                            .cursor(Cursor.Pointer)
                            .backgroundColor(
                                if (isSelected) AppColors.Primary.toRgbString().let {
                                    com.varabyte.kobweb.compose.ui.graphics.Color.rgba(0, 162, 255, 0.1f)
                                } else colors.surface
                            )
                            .transition(
                                Transition.of("background-color", Constants.ANIMATION_FAST.ms)
                            )
                            .onClick {
                                onSelect(option)
                                isOpen = false
                            }
                            .styleModifier {
                                property("border-bottom", "1px solid ${colors.border.toRgbString()}")
                            }
                    ) {
                        SpanText(
                            text = option.label,
                            modifier = Modifier
                                .sFontSize(14)
                                .color(
                                    if (isSelected) AppColors.Primary
                                    else colors.textPrimary
                                )
                        )
                    }
                }
            }
        }

        error?.let {
            SpanText(
                text = it,
                modifier = Modifier
                    .sFontSize(12)
                    .color(AppColors.Error)
                    .sMarginTop(4)
            )
        }
    }
}
